/* public-i18n-migrated */
import { useI18n } from "@/i18n";
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import { localizedBbqEvent } from '@/lib/bbqLocale';
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, Printer, Download, Calendar, Clock, MapPin, Flame } from "lucide-react";
import { toast } from "sonner";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSTER_URL = "https://allrounderound.com/bbq";

export default function BbqPosterPage() {
  const { tx, lang } = useI18n();

  const [eventSource, setEvent] = useState({
    title: "South Bank BBQ Party",
    date_label: "Fri, Aug 7",
    date_note: "Rain or shine — covered pavilion secured",
    time_label: "5:00 – 8:00 PM",
    time_note: "Arrive from 4:45",
    location_label: "South Bank Park",
    location_note: "Riverside pavilion · closest to the Wheel",
  });
  const event = localizedBbqEvent(eventSource, lang);
  const qrRef = useRef(null);
  const [prices, setPrices] = useState({ bbq_only: 30, bbq_beer: 32 });

  useEffect(() => {
    document.title = "BBQ Poster · All Rounder";
    axios.get(`${API}/bbq-event`).then((r) => setEvent((p) => ({ ...p, ...r.data }))).catch(() => {});
    axios.get(`${API}/bbq-pricing`).then((r) => setPrices((p) => ({ ...p, ...(r.data.prices || {}) }))).catch(() => {});
  }, []);

  const onPrint = () => window.print();
  const onDownload = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return toast.error(tx("QR not ready"));
    const link = document.createElement("a");
    link.download = "bbq-qr.png";
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(tx("QR PNG downloaded"));
  };

  return (
    <main data-testid="bbq-poster-page" className="min-h-screen bg-neutral-100 py-10 px-4 print:p-0 print:bg-white">
      {/* Controls — hidden on print */}
      <div className="max-w-[880px] mx-auto flex flex-wrap gap-3 items-center justify-between mb-6 print:hidden">
        <PublicLanguageSelect id="poster-language"/>
        <Link to="/bbq" className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-[#E85A2C] transition-colors" data-testid="poster-back">
          <ArrowLeft className="w-4 h-4" /> {tx("Back to BBQ page")} </Link>
        <div className="flex items-center gap-2">
          <button onClick={onDownload} data-testid="poster-download-qr" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-neutral-300 text-neutral-700 hover:border-[#E85A2C] hover:text-[#E85A2C] transition-colors flex items-center gap-2">
            <Download className="w-3.5 h-3.5" /> QR PNG
          </button>
          <button onClick={onPrint} data-testid="poster-print" className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2">
            <Printer className="w-3.5 h-3.5" /> {tx("Print / Save PDF")} </button>
        </div>
      </div>

      {/* Poster A4-ish landscape card */}
      <article
        data-testid="poster-card"
        className="max-w-[880px] mx-auto bg-[#141010] text-white rounded-lg overflow-hidden shadow-2xl print:shadow-none print:rounded-none border border-white/10 print:border-0"
        style={{ fontFamily: "'Fraunces', serif" }}
      >
        {/* Top orange band */}
        <div className="bg-[#E85A2C] px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame className="w-5 h-5 text-white" />
            <span className="text-white text-[11px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "sans-serif" }}>
              All Friends × All Rounder
            </span>
          </div>
          <span className="text-white text-[11px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "sans-serif" }}> {tx("RSVP · Spots Limited")} </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr]">
          {/* LEFT: title + benefits */}
          <div className="px-8 sm:px-10 py-10">
            <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]" style={{ fontFamily: "sans-serif" }}> {tx("// Event Poster")} </p>
            <h1 className="mt-3 text-white text-5xl sm:text-6xl font-bold tracking-tight leading-[0.95]">
              {event.title || "South Bank BBQ Party"}
            </h1>

            <p className="mt-6 text-white/80 text-lg leading-relaxed" style={{ fontFamily: "sans-serif" }}> {tx("BBQ, drinks, snacks & new friends.")}<br /> {tx("Just bring yourself and join the fun!")} </p>

            <div className="mt-8 space-y-2">
              <div className="text-[#E85A2C] text-2xl sm:text-3xl font-bold tracking-tight">{tx("FOOD · FRIENDS · FUN")}</div>
              <div className="text-white text-lg font-bold" style={{ fontFamily: "sans-serif" }}> {tx("Unmissable event in Brisbane")} </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 text-[12px] leading-snug" style={{ fontFamily: "sans-serif" }}>
              <div className="p-3 rounded-sm border border-white/10 bg-white/[0.03]">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#E85A2C] mb-1">{tx("Everything Included")}</div>
                <div className="text-white/80">{tx("Meat · Sides · Soft Drinks · Equipment")}</div>
              </div>
              <div className="p-3 rounded-sm border border-white/10 bg-white/[0.03]">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#E85A2C] mb-1">{tx("Ticket Price")}</div>
                <div className="text-white/80">{tx("BBQ Only · $")}{prices.bbq_only} {tx("per person")}</div>
              </div>
              <div className="p-3 rounded-sm border border-white/10 bg-white/[0.03]">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#E85A2C] mb-1">{tx("Group Discount")}</div>
                <div className="text-white/80">{tx("Save up to $2 per person")}</div>
              </div>
            </div>
          </div>

          {/* RIGHT: date/time/loc + QR */}
          <div className="px-8 sm:px-10 py-10 bg-[#0F0B08] border-l border-white/10 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <AllRounderLogo variant="mark" color="#FAFAFA" size={36} />
              <div className="text-white text-lg font-bold tracking-widest uppercase" style={{ fontFamily: "sans-serif", letterSpacing: "0.24em" }}>
                All Rounder
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-[#E85A2C] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-white/50" style={{ fontFamily: "sans-serif" }}>{tx("Date")}</div>
                  <div className="text-white text-lg font-bold leading-tight">{event.date_label}</div>
                  {event.date_note && <div className="text-white/50 text-[11px]" style={{ fontFamily: "sans-serif" }}>{event.date_note}</div>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#E85A2C] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-white/50" style={{ fontFamily: "sans-serif" }}>{tx("Time")}</div>
                  <div className="text-white text-lg font-bold leading-tight">{event.time_label}</div>
                  {event.time_note && <div className="text-white/50 text-[11px]" style={{ fontFamily: "sans-serif" }}>{event.time_note}</div>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#E85A2C] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[9px] uppercase tracking-[0.24em] font-bold text-white/50" style={{ fontFamily: "sans-serif" }}>{tx("Location")}</div>
                  <div className="text-white text-lg font-bold leading-tight">{event.location_label}</div>
                  {event.location_note && <div className="text-white/50 text-[11px]" style={{ fontFamily: "sans-serif" }}>{event.location_note}</div>}
                </div>
              </div>
            </div>

            {/* QR block */}
            <div className="mt-auto">
              <div ref={qrRef} data-testid="poster-qr" className="bg-white rounded-md p-3 mx-auto w-fit">
                <QRCodeCanvas
                  value={POSTER_URL}
                  size={220}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#141010"
                  imageSettings={{
                    src:
                      "data:image/svg+xml;utf8," +
                      encodeURIComponent(`
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>
                          <rect width='100' height='100' rx='16' fill='#E85A2C'/>
                          <text x='50' y='66' text-anchor='middle' font-family='Fraunces, serif' font-weight='700' font-size='42' fill='#FFFFFF'>AR</text>
                        </svg>
                      `),
                    height: 42,
                    width: 42,
                    excavate: true,
                  }}
                />
              </div>
              <div className="mt-3 text-center">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]" style={{ fontFamily: "sans-serif" }}> {tx("Scan to RSVP")} </div>
                <div className="mt-1 text-white/80 text-[11px]" style={{ fontFamily: "sans-serif" }}>
                  allrounderound.com/bbq
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom band */}
        <div className="bg-[#0A0806] px-8 py-3 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] font-bold text-white/60" style={{ fontFamily: "sans-serif" }}>
          <span>#AllRounder · #SouthBankBBQ</span>
          <span className="text-[#E85A2C]">{tx("Don't wait · Book now")}</span>
        </div>
      </article>

      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body { background: #fff !important; }
        }
      `}</style>
    </main>
  );
}
